import {
  CreateFlagDefinitionInput,
  FlagDefinition,
  UpdateFlagDefinitionInput,
} from '@/API';
import { FlagDefinitionRepository } from '@/repository/FlagDefinitionRepository';
import { GenericService } from '@/service/GenericService';
import log from 'loglevel';

/**
 * Service for managing FlagDefinition records
 */
export class FlagDefinitionService extends GenericService<FlagDefinition> {
  protected repository: FlagDefinitionRepository;

  constructor() {
    const repository = new FlagDefinitionRepository();
    super(repository);
    this.repository = repository;
  }

  /**
   * Gets flag definitions by list type and field name
   */
  async getFlagDefinitionsByField(
    listType: string,
    fieldName: string
  ): Promise<FlagDefinition[]> {
    try {
      const result = await this.search({
        filter: {
          and: [
            { listType: { eq: listType } },
            { fieldName: { eq: fieldName } },
          ],
        },
        limit: 1000,
      });
      return result?.items || [];
    } catch (error) {
      log.error(
        'FlagDefinitionService getFlagDefinitionsByField error:',
        error
      );
      throw error;
    }
  }

  /**
   * Creates a new flag definition with duplicate checking
   */
  async createFlagDefinition(
    input: CreateFlagDefinitionInput
  ): Promise<FlagDefinition> {
    try {
      // Check if flag definition already exists
      const existing = await this.search({
        filter: {
          and: [
            { listType: { eq: input.listType } },
            { fieldName: { eq: input.fieldName } },
            { flagValue: { eq: input.flagValue } },
          ],
        },
      });

      if (existing?.items && existing.items.length > 0) {
        throw new Error(
          `Flag definition already exists for ${input.listType}.${input.fieldName}.${input.flagValue}`
        );
      }

      return await this.save(input);
    } catch (error) {
      log.error('FlagDefinitionService createFlagDefinition error:', error);
      throw error;
    }
  }

  /**
   * Updates an existing flag definition
   */
  async updateFlagDefinition(
    input: UpdateFlagDefinitionInput
  ): Promise<FlagDefinition> {
    try {
      return await this.repository.update(input);
    } catch (error) {
      log.error('FlagDefinitionService updateFlagDefinition error:', error);
      throw error;
    }
  }

  /**
   * Creates multiple default flag definitions for a field
   */
  async createDefaultFlagDefinitions(
    listType: string,
    fieldName: string,
    flagValues: string[]
  ): Promise<FlagDefinition[]> {
    try {
      const existingFlags = await this.getFlagDefinitionsByField(
        listType,
        fieldName
      );
      const existingValues = new Set(existingFlags.map(f => f.flagValue));

      const newFlags: CreateFlagDefinitionInput[] = flagValues
        .filter(value => !existingValues.has(value))
        .map(value => ({
          listType,
          fieldName,
          flagValue: value,
          friendlyName: value, // Default to the flag value itself
        }));

      const results: FlagDefinition[] = [];
      for (const flagInput of newFlags) {
        try {
          const created = await this.createFlagDefinition(flagInput);
          results.push(created);
        } catch (error) {
          log.warn(
            `Failed to create flag definition for ${flagInput.flagValue}:`,
            error
          );
        }
      }

      return results;
    } catch (error) {
      log.error(
        'FlagDefinitionService createDefaultFlagDefinitions error:',
        error
      );
      throw error;
    }
  }
}
